<script lang="ts">
  import type { SpotPriser } from "$lib/types/spotPriser";
  import type { Omraade } from "$lib/types/omraade";
  import { selectAreas } from "$lib/utils/areaUtils";
  import Meta from "$lib/MetaData.svelte";
  import type { MetaData } from "$lib/types/metaData";
  import theTime from "$lib/utils/the-time";
  import { getHoursOfUTCDateTimeStringFromDateDay } from "$lib/utils/time";
  import AreaSelect from "./AreaSelect.svelte";
  import fetchSpotprisTime from "./fetchSpotprisTime";
  import HoursList from "./HoursPriceListD3.svelte";
  import getAverage from "$lib/utils/getAverage";
  import PriceBarChart from "./PriceBarChart.svelte";
  import TheTimeGoes from "./TheTimeGoes.svelte";

  const initArea = selectAreas[0].urlLabel;
  const getDateOut = (date: Date, opt?: Intl.DateTimeFormatOptions) =>
    date.toLocaleDateString("nb-NO", {
      ...{ month: "short", day: "numeric" },
      ...(opt ? opt : {}),
    });
  const dateOne = new Date(); // new Date("2024-12-11");
  const title = "Timespris, spotprisen/strømprisen i Norge";
  const description = "Følg med på spotprisen i Norge, time for time.";

  let {
    data,
    children,
  }: {
    data: {
      fallbackMeta?: MetaData;
      // area?: string; // urlLabel
    };
    children?: import("svelte").Snippet;
  } = $props();

  let mmState: string | null = $state(null);
  let ssGoes = $state(dateOne);
  let mmGoes = $derived(theTime(ssGoes).to("mm"));
  let hhGoes = $derived(theTime(ssGoes).to("HH"));
  let todayString = $derived(theTime(ssGoes).toPlainDateString());
  let tomorrowDate = $derived(theTime(todayString).goes({ DD: +1 }));
  let selectedDate: Date = $state(dateOne);
  const hourList: string[] = $derived(getHoursOfUTCDateTimeStringFromDateDay(selectedDate));
  const hours = $derived(hourList.map((x) => +theTime(x).to("HH")));
  const dayParts = $derived([
    {
      label: "Natt",
      hours: hours.slice(0, 6),
      hoursIndex: [0, 1, 2, 3, 4, 5],
    },
    {
      label: "Morgen/dag",
      hours: hours.slice(6, 15),
      hoursIndex: [6, 7, 8, 9, 10, 11, 12, 13, 14],
    },
    {
      label: "Middag/kveld",
      hours: hours.slice(15, hours.length),
      hoursIndex: hours.slice(15, hours.length).map((_, i) => i + 15),
    },
  ]);

  let viewNumbers:
    | undefined
    | {
        spotPriser?: Readonly<SpotPriser>[];
        error?: boolean;
        message?: string;
      } = $state();

  let selectedArea = $state(initArea);
  let inclStromstotte: boolean = $state(false);
  let spotPriser: SpotPriser[] | undefined = $state();
  let isTodaySelected = $derived(theTime(selectedDate).toPlainDateString() === todayString);
  let currentHourIndex = $derived(
    hourList.indexOf(theTime(todayString, { T: 0, HH: hhGoes }).toISOString())
  );
  let areaCode = $derived(selectAreas.find((x: Omraade) => x.urlLabel === selectedArea)?.value);
  let spotPriserOmraade = $derived(spotPriser?.find((x) => x.omrade === areaCode));
  let spotPriserToday = $derived(spotPriserOmraade?.dager.find((x) => x.dato === todayString));
  let spotPriserTomorrow = $derived(
    spotPriserOmraade?.dager?.find((x) => x.dato === theTime(tomorrowDate).toPlainDateString())
  );
  let prices = $derived.by(() => {
    if (!isTodaySelected && spotPriserTomorrow?.priser) {
      return spotPriserTomorrow.priser;
    } else if (spotPriserToday?.priser) {
      return spotPriserToday.priser;
    } else {
      return [];
    }
  });
  let stromstotteApplies = $derived(prices?.some((x) => x.hasStromstottePris));
  let showRatio = $state(true);

  const updateNumbers = async () => {
    viewNumbers = await fetchSpotprisTime({
      date: todayString,
      // Check if UA supports AbortSignal
      ...(typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout !== "undefined"
        ? { signal: AbortSignal.timeout(30000) }
        : {}),
    });

    if (viewNumbers && !viewNumbers.error && viewNumbers.spotPriser) {
      spotPriser = viewNumbers.spotPriser;
    }
  };

  $effect(() => {
    if (!mmState || mmGoes !== mmState) {
      mmState = mmGoes;
      updateNumbers();
    }
  });
</script>

<TheTimeGoes bind:ssGoes />

<Meta data={{ ...(data?.fallbackMeta || {}), title, description }} />

<div class="pb-2 mx-edge-x">
  <p class="max-laptop:sr-only my-0 inline-block mr-4">Prisområde:</p>
  <AreaSelect
    class="flex max-laptop:justify-between laptop:inline-flex items-end gap-2 m-0"
    {initArea}
    bind:selectedArea
  />
</div>

<p
  class="pl-edge-x pt-1 my-0 whitespace-nowrap children-mr-2 children-whitespace-nowrap overflow-hidden"
>
  <span class="max-laptop:sr-only inline-block mr-4">Dato:</span>
  <button
    class="button min-w-0"
    class:button-primary={!isTodaySelected}
    class:button-selected={isTodaySelected}
    onclick={() => {
      selectedDate = theTime(todayString);
    }}
  >
    I dag,
    <span class="font-400">{getDateOut(theTime(todayString))}</span>
  </button><button
    class="button min-w-0"
    aria-disabled={spotPriserTomorrow === undefined}
    class:button-link={spotPriserTomorrow === undefined}
    class:px-0={spotPriserTomorrow === undefined}
    class:button-selected={!isTodaySelected}
    class:button-primary={isTodaySelected}
    onclick={() => {
      if (spotPriserTomorrow !== undefined) {
        selectedDate = tomorrowDate;
      }
    }}
  >
    <span class:hidden={spotPriserTomorrow === undefined} class="landscape:inline font-bold"
      >I morgen,</span
    >
    <span class:font-400={spotPriserTomorrow !== undefined}>{getDateOut(tomorrowDate)}</span>
    {#if spotPriserTomorrow === undefined}
      <span class="font-400">tilgjengelige kl 13</span>
    {/if}
  </button>
</p>

<PriceBarChart
  {prices}
  includeStotte={inclStromstotte}
  {showRatio}
  currentHourIndex={isTodaySelected ? currentHourIndex : null}
  {selectedDate}
>
  {#snippet title()}
    <span id="graf-tittel"
      ><span class="whitespace-nowrap">Øre/kWh,</span>
      <span class="whitespace-nowrap">{getDateOut(selectedDate)}</span>

      <span class="max-landscape:sr-only">
        for {selectAreas.find((x) => x.value === areaCode)?.label || ""}/{areaCode}
      </span>
    </span>
  {/snippet}

  {#snippet openSettings()}
    <p
      class="mb-0 text-sm grid grid-cols-[auto_1fr] gap-y-2 gap-x-3"
      role="group"
      aria-labelledby="graf-tittel"
    >
      <button
        role="checkbox"
        aria-checked={showRatio}
        class="button button-link border-none min-h-3rem min-w-0 px-0 inline-flex gap-x-3px"
        onclick={() => (showRatio = !showRatio)}
      >
        Forhold billigst-dyrest
      </button>
      <button
        role="checkbox"
        aria-checked={inclStromstotte}
        class="button button-link border-none min-h-3rem min-w-0 px-0 inline-flex gap-x-3px"
        onclick={() => (inclStromstotte = !inclStromstotte)}
      >
        <span class="text-start">
          Med strømstøtte
          <span class="font-normal after:content-['*'] after:font-bold"
            >{!stromstotteApplies
              ? ` (ingen for ${selectAreas.find((x) => x.value === areaCode)?.shortLabel || ""}/${areaCode} ${getDateOut(selectedDate)})`
              : ``}</span
          >

          {#if stromstotteApplies && inclStromstotte}
            <span
              class="inline-block w-5 h-1em ml-1 translate-y-1/6"
              style="background-image: var(--img-stotted);"
            ></span>
          {/if}
        </span>
      </button>
    </p>
  {/snippet}
</PriceBarChart>

<section class="mx-edge-x desktop:px-6" id="timespris-tabell">
  {#if viewNumbers?.error && viewNumbers?.message}
    <p class="test-sm"><strong class="italic">Feil ved lasting av priser</strong></p>
    <p class="test-sm">{viewNumbers.message}</p>
    <hr />
  {/if}

  <p class="italic text-sm mb-0 text-[--fg-muted] leading-tight">
    <strong>*</strong>Strømstøtte for husholdninger (gjelder ikke hytter) –
    {#if selectedDate.getFullYear() >= 2026}
      <a href="/om/aktuelt/endring-i-den-ordinere-stromstotten"
        >for 2026 kompenseres 90% over 77 øre/kWh eksklusiv mva.</a
      >
    {:else if selectedDate.getFullYear() >= 2025}
      <a href="/om/aktuelt/nyttig-info-om-stromstotten-fra-regjeringen"
        >for 2025 kompenseres 90% over 75 øre/kWh eksklusiv mva.</a
      >
    {:else if selectedDate.getFullYear() >= 2024}
      <a href="/om/aktuelt/nyttig-info-om-stromstotten-fra-regjeringen">
        for 2024 kompenseres 90% over 73 øre/kWh eksklusiv mva.
      </a>
    {/if}
  </p>

  <p class="italic text-sm mt-0 text-[--fg-muted] leading-tight">
    Du kan også velge <u>støtteordningen norgespris</u> som gir deg en fast strømpris på 50 øre/kWh
    inkl. mva.
    <a href="https://www.ishavskraft.no/norgespris">Les mer om norgespris</a>
  </p>

  <h2 class="text-1em mt-5">
    {!isTodaySelected ? "I morgen," : "I dag,"}
    {getDateOut(!isTodaySelected ? tomorrowDate : theTime(todayString))}
    for
    {selectAreas.find((x) => x.value === areaCode)?.label || ""}/{areaCode}
  </h2>

  {@render children?.()}

  <div class="mb-8">
    {#each dayParts as { label, hours, hoursIndex } (label)}
      {@const average = getAverage(
        prices?.filter((_x, i) => hoursIndex.includes(i)).map((x) => x.pris) || []
      )
        .toFixed(2)
        .replace(".", ",")}
      <details
        open={!isTodaySelected ||
          hoursIndex.includes(currentHourIndex) ||
          hours[0] > +theTime(hourList[currentHourIndex]).to("HH")}
        class="mt-1 mb-3 max-w-fit"
      >
        <summary class="mt-2 mb-1">
          <strong class="">{label}</strong>
          <small class="font-400">{average} øre/kWh i snitt</small>
        </summary>
        <HoursList
          includeStotte={inclStromstotte}
          {showRatio}
          {hours}
          {hoursIndex}
          {prices}
          currentHour={isTodaySelected ? currentHourIndex : null}
        />
      </details>
    {/each}
  </div>

  <div class="children-my-2 my-6 text-sm text-center">
    <p>
      Timespris sist hentet kl {hhGoes}:{mmState}.
    </p>
    <p>Morgendagens priser er tidligst tilgjengelig kl 13:00 dagen før.</p>
  </div>
</section>

<style lang="postcss">
  button[role="checkbox"] {
    &::before {
      border-radius: 0;
      background-color: var(--_box-fill, transparent);
      box-shadow:
        inset 0 0 0 1px,
        inset 0 0 0 4px var(--_box-outline, transparent);
      content: "";
      display: inline-block;
      margin-inline-end: var(--s-1);
      transform: translateY(17.5%);
      width: 1em;
      min-width: 1em;
      height: 1em;
    }

    &[aria-checked="true"]::before {
      --_box-fill: currentcolor;
      --_box-outline: var(--glod);
    }
  }
</style>
